import { useEffect, useState } from 'react';

/** Below this, a shrunk visual viewport is the URL bar collapsing, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * The height a `fixed inset-0` shell should take while the on-screen keyboard is up, in pixels.
 *
 * The shell anchors to the LAYOUT viewport, which iOS leaves at full height when the keyboard
 * opens — so the bottom of the shell, including the scroll region's lower half and the action
 * bar, sits underneath the keyboard with no way to reach it. The VISUAL viewport does shrink,
 * so driving the shell's height from it keeps everything reachable.
 *
 * Returns null whenever the keyboard is down, and where the API is unavailable — callers fall
 * back to their existing CSS height rather than guessing. Null is the important half: a shell
 * that is only ever told to shrink while the keyboard is measurably up cannot get stuck short.
 */
export function useVisualViewportHeight(): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let settle = 0;

    // 1. Measure the gap, not the viewport: only a keyboard-sized shrink overrides the shell.
    const measure = () => {
      const shrink = window.innerHeight - viewport.height;
      setHeight(shrink > KEYBOARD_MIN_HEIGHT ? viewport.height : null);
    };

    // 2. Blurring a field closes the keyboard over ~250ms, and iOS does not reliably fire a
    //    resize once it has gone — without this the shell keeps the short height it was last
    //    given, and the action bar stays stranded halfway up the screen.
    const measureAfterKeyboard = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(measure, 400);
    };

    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    window.addEventListener('focusout', measureAfterKeyboard);

    return () => {
      window.clearTimeout(settle);
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
      window.removeEventListener('focusout', measureAfterKeyboard);
    };
  }, []);

  return height;
}
